import React from 'react';
import { Plus, X } from 'lucide-react';

const PRESETS = ['Transport', 'Loading', 'Unloading'];

/**
 * ExtraChargesEditor — bill-level non-line charges.
 *
 * Renders the 3 preset rows (Transport / Loading / Unloading) always, then
 * any custom rows the user added. Amounts of 0 are still kept in state so
 * the layout stays stable while the user types — the backend filters out
 * zero-amount rows on save.
 *
 * Props:
 *   - charges: [{label, amount}]
 *   - onChange: (next) => void
 *   - itemsTotal: number  (sum of item-level totals to display the running grand total)
 */
export default function ExtraChargesEditor({ charges = [], onChange, itemsTotal = 0 }) {
  // Map presets → existing row OR a zero placeholder.
  const map = new Map(charges.map(c => [c.label, c]));
  const presetRows = PRESETS.map(p => map.get(p) || { label: p, amount: '' });
  const customRows = charges.filter(c => !PRESETS.includes(c.label));

  const setField = (label, patch) => {
    const next = [...charges];
    const idx = next.findIndex(c => c.label === label);
    if (idx === -1) {
      next.push({ label, amount: '', ...patch });
    } else {
      next[idx] = { ...next[idx], ...patch };
    }
    onChange(next);
  };

  const removeRow = (label) => {
    onChange(charges.filter(c => c.label !== label));
  };

  const addCustom = () => {
    const placeholder = `Charge ${customRows.length + 1}`;
    onChange([...charges, { label: placeholder, amount: '' }]);
  };

  const extrasTotal = [...presetRows, ...customRows]
    .reduce((s, c) => s + (parseFloat(c.amount) || 0), 0);
  const grand = itemsTotal + extrasTotal;

  return (
    <div className="mt-4 border border-zinc-800 rounded-xl p-3 bg-zinc-950/40" data-testid="extra-charges-editor">
      <div className="flex items-center justify-between mb-2">
        <div className="text-zinc-300 text-xs font-semibold">Extra Charges</div>
        <button type="button" onClick={addCustom}
          data-testid="add-extra-charge"
          className="text-[10px] px-2 py-1 rounded-md bg-zinc-800 hover:bg-zinc-700 text-zinc-300 border border-zinc-700 inline-flex items-center gap-1">
          <Plus size={10}/> Add Charge
        </button>
      </div>

      <div className="space-y-1.5">
        {presetRows.map(row => (
          <div key={row.label} className="flex items-center gap-2" data-testid={`extra-row-${row.label.toLowerCase()}`}>
            <div className="w-32 text-zinc-400 text-[11px]">{row.label}</div>
            <span className="text-zinc-600 text-[11px]">₹</span>
            <input type="number" step="any" value={row.amount}
              onChange={e => setField(row.label, { amount: e.target.value })}
              placeholder="0"
              data-testid={`extra-amt-${row.label.toLowerCase()}`}
              className="flex-1 max-w-[140px] bg-zinc-900 border border-zinc-700 rounded-md px-2 py-1 text-zinc-100 text-xs focus:outline-none focus:border-amber-500" />
          </div>
        ))}

        {customRows.map((row, idx) => (
          <div key={`custom-${idx}-${row.label}`} className="flex items-center gap-2" data-testid={`extra-row-custom-${idx}`}>
            <input value={row.label}
              onChange={e => {
                const newLabel = e.target.value;
                const next = [...charges];
                const target = next.findIndex(c => c === row || (c.label === row.label && !PRESETS.includes(c.label)));
                if (target !== -1) next[target] = { ...next[target], label: newLabel };
                onChange(next);
              }}
              placeholder="Custom label"
              className="w-32 bg-zinc-900 border border-zinc-700 rounded-md px-2 py-1 text-zinc-100 text-xs focus:outline-none focus:border-amber-500" />
            <span className="text-zinc-600 text-[11px]">₹</span>
            <input type="number" step="any" value={row.amount}
              onChange={e => {
                const next = [...charges];
                const target = next.findIndex(c => c === row || (c.label === row.label && !PRESETS.includes(c.label)));
                if (target !== -1) next[target] = { ...next[target], amount: e.target.value };
                onChange(next);
              }}
              placeholder="0"
              className="flex-1 max-w-[140px] bg-zinc-900 border border-zinc-700 rounded-md px-2 py-1 text-zinc-100 text-xs focus:outline-none focus:border-amber-500" />
            <button type="button" onClick={() => removeRow(row.label)}
              className="p-1 rounded bg-red-900/30 text-red-400 hover:bg-red-900/50">
              <X size={10}/>
            </button>
          </div>
        ))}
      </div>

      <div className="mt-3 pt-2 border-t border-zinc-800 flex items-center justify-between text-[11px]">
        <span className="text-zinc-500">Items ₹{itemsTotal.toFixed(2)} + Extras ₹{extrasTotal.toFixed(2)}</span>
        <span className="text-amber-400 font-semibold" data-testid="extras-grand-total">Grand Total: ₹{grand.toFixed(2)}</span>
      </div>
    </div>
  );
}
